/**
 * EconomicsTab Component
 * AIDLC Administrative Backend
 *
 * What the project has cost in agent tokens against what the CRM says it
 * earns: revenue, AI cost, margin and ROI, then the spend by agent and by
 * model. Revenue comes from the CRM through the integration API; when it
 * has not been sent the margin and ROI are shown as unknown, not as a figure
 * computed against zero.
 */

import { useState, useEffect } from 'react';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { EmptyState } from '../../shared/EmptyState';
import { getProjectEconomics } from '../../../services/projectService';
import { SprintEconomics } from './SprintEconomics';
import type { ProjectEconomics, EconomicsSlice } from '../../../types/project';

interface EconomicsTabProps {
  projectId: string;
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const count = new Intl.NumberFormat('en-US');

function money(value: number | null): string {
  return value === null ? 'Not sent' : usd.format(value);
}

function percent(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}

function ratio(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(2)}×`;
}

function tokensOf(slice: EconomicsSlice): number {
  return slice.inputTokens + slice.outputTokens + slice.cacheReadTokens + slice.cacheCreationTokens;
}

function Figure({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: 'good' | 'bad' }) {
  const valueClass = tone === 'good' ? 'text-emerald-600' : tone === 'bad' ? 'text-red-600' : 'text-black dark:text-white';
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-2xl font-semibold mt-1 tabular-nums ${valueClass}`}>{value}</div>
      {note && <div className="text-[11px] text-gray-500 mt-1">{note}</div>}
    </div>
  );
}

function SliceTable<T extends EconomicsSlice>({ title, rows, nameOf }: { title: string; rows: T[]; nameOf: (row: T) => string }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-800">
      <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 text-xs font-semibold text-black dark:text-white">{title}</div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-xs text-gray-500">No agent work has been recorded against this project yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-right px-4 py-2 font-medium">Sessions</th>
                <th className="text-right px-4 py-2 font-medium">Tokens</th>
                <th className="text-right px-4 py-2 font-medium">Cache reads</th>
                <th className="text-right px-4 py-2 font-medium">Cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={nameOf(row)} className="border-t border-zinc-100 dark:border-zinc-800">
                  <td className="px-4 py-2 text-black dark:text-white">{nameOf(row)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{count.format(row.sessions)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{count.format(tokensOf(row))}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{count.format(row.cacheReadTokens)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{usd.format(row.costUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function EconomicsTab({ projectId }: EconomicsTabProps) {
  const [economics, setEconomics] = useState<ProjectEconomics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getProjectEconomics(projectId);
        if (!cancelled) setEconomics(data);
      } catch (err) {
        console.error('[EconomicsTab] Failed to load economics:', err);
        if (!cancelled) setError('The economics could not be loaded. Try again in a moment.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  if (isLoading) return <LoadingSpinner />;
  if (error || !economics) {
    return <EmptyState title="Economics unavailable" description={error ?? 'Nothing came back for this project.'} />;
  }

  const { usage, customerRef, revenueUsd, marginUsd, marginPct, roi } = economics;
  const marginTone = marginUsd === null ? undefined : marginUsd >= 0 ? 'good' : 'bad';

  return (
    <div className="space-y-6">
      {/* Estimate against actual per sprint: the project figures below are the
          same question asked of the whole project. */}
      <SprintEconomics projectId={projectId} />

      {/* Commercial source */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
        {customerRef ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Customer</div>
              <div className="mt-1 font-medium text-black dark:text-white">{customerRef.customerName}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Product</div>
              <div className="mt-1 font-medium text-black dark:text-white">{customerRef.crmProductName ?? '—'}</div>
              {customerRef.crmProductSku && <div className="text-[11px] text-gray-500">{customerRef.crmProductSku}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Quote</div>
              <div className="mt-1 font-medium text-black dark:text-white">{customerRef.crmQuoteNumber ?? '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Invoice</div>
              <div className="mt-1 font-medium text-black dark:text-white">{customerRef.crmInvoiceNumber ?? 'Not yet invoiced'}</div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            This project was not opened from the CRM, so it has no customer, quote or revenue on record. Its AI cost is still tracked below.
          </div>
        )}
      </div>

      {/* Headline figures */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Figure label="Revenue" value={money(revenueUsd)} note={revenueUsd === null ? 'The CRM has not sent a figure' : 'Agreed in the CRM'} />
        <Figure label="AI cost" value={usd.format(usage.costUsd)} note={`${count.format(usage.sessions)} agent sessions`} />
        <Figure label="Margin" value={marginUsd === null ? '—' : usd.format(marginUsd)} note={marginUsd === null ? 'Needs revenue' : percent(marginPct)} tone={marginTone} />
        <Figure label="ROI" value={ratio(roi)} note={roi === null ? (usage.costUsd === 0 ? 'No cost yet' : 'Needs revenue') : 'Margin over AI cost'} tone={marginTone} />
      </div>

      {/* Token detail */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Figure label="Input tokens" value={count.format(usage.inputTokens)} />
        <Figure label="Output tokens" value={count.format(usage.outputTokens)} />
        <Figure label="Cache reads" value={count.format(usage.cacheReadTokens)} note="Priced at a tenth of input" />
        <Figure label="Cache writes" value={count.format(usage.cacheCreationTokens)} note="Priced at 1.25× input" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SliceTable title="Spend by agent" rows={economics.byAgent} nameOf={(r) => r.agentName} />
        <SliceTable title="Spend by model" rows={economics.byModel} nameOf={(r) => r.model} />
      </div>

      <div className="text-[11px] text-gray-500">
        {usage.lastActivityAt
          ? `Last agent activity ${new Date(usage.lastActivityAt).toLocaleString()}. Costs are the CLI's own figure where it reports one, otherwise priced from the token counts at the configured rates.`
          : 'No agent activity has been attributed to this project. Work is attributed through the backlog item an agent is working, so items filed against this project count toward it.'}
      </div>
    </div>
  );
}
